import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import ProtectedRoute from "../components/ProtectedRoute";
import UserStatsCard from "../components/UserStatsCard";
import ChallengesPanel from "../components/ChallengesPanel";
import UserAnalytics from "../components/UserAnalytics";
import SubscriptionCard from "../components/SubscriptionCard";
import { Avatar } from "../components/Avatar";
import { avatarForLevel } from "../constants/avatars";
import type { GamificationStatsResponse, Challenge } from "../services/api";
import { apiService } from "../services/api";
import { logger } from "../utils/logger";
import { useAuth } from "../hooks/useAuth";
import "../css/Dashboard.css";
import "../css/Courses.css";
import ConfettiEffect from "../components/ConfettiEffect";

function Dashboard() {
  const search = Route.useSearch();
  const { refreshUser } = useAuth();
  const [stats, setStats] = useState<GamificationStatsResponse | null>(null);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDevConfetti, setShowDevConfetti] = useState(false);
  const levelAvatarId = stats ? avatarForLevel(stats.user.level ?? 1) : avatarForLevel(1);
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 640
  );
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Check if this is a successful payment redirect
  const paymentSuccess = search?.payment === 'success' || showDevConfetti;

  const isDev = import.meta.env.DEV;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [statsData, challengesData] = await Promise.all([
          apiService.getGamificationStats().catch((err) => {
            logger.warn('Failed to load gamification stats', err);
            return null;
          }),
          apiService.getActiveChallenges().catch((err) => {
            logger.warn('Failed to load challenges', err);
            return [];
          }),
        ]);

        setStats(statsData);
        setChallenges(challengesData);
      } catch (err) {
        logger.error('Failed to load dashboard data', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Refresh user data when payment succeeds to get updated subscription status
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (paymentSuccess) {
        try {
          logger.info('Payment success detected - refreshing user data');
          await refreshUser();
          logger.info('User data refreshed after payment');
        } catch (err) {
          logger.error('Failed to refresh user after payment', err);
        }
      }
    };
    
    handlePaymentSuccess();
  }, [paymentSuccess, refreshUser]);

  // Auto-dismiss payment success message after 3 seconds
  useEffect(() => {
    if (paymentSuccess) {
      const timer = setTimeout(() => {
        setShowDevConfetti(false);
        // Clear the payment query param if it exists
        if (search?.payment === 'success') {
          window.history.replaceState({}, '', '/dashboard');
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess, search]);

  const handleRefresh = async () => {
    const [statsData, challengesData] = await Promise.all([
      apiService.getGamificationStats(),
      apiService.getActiveChallenges(),
    ]);
    setStats(statsData);
    setChallenges(challengesData);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-error">
          <h2>Failed to load dashboard</h2>
          <p>{error}</p>
          <button onClick={handleRefresh}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {paymentSuccess && <ConfettiEffect />}
      {paymentSuccess && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.95), rgba(74, 222, 128, 0.95))',
          color: '#0a0f1a',
          padding: '32px 48px',
          borderRadius: '16px',
          textAlign: 'center',
          zIndex: 9999,
          boxShadow: '0 20px 60px rgba(34, 211, 238, 0.4)',
          animation: 'slideDown 0.5s ease-out',
        }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 8 }}>🎉 Payment Successful!</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>Welcome to Premium! Enjoy all the features.</p>
        </div>
      )}
      <div
        style={{
          position: 'relative',
          marginBottom: 32,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <Link className="link-button" to="/courses">
          ← Back to Courses
        </Link>
        {isDev && !paymentSuccess && (
          <button
            onClick={() => setShowDevConfetti(true)}
            className="link-button"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              border: '2px dashed rgba(255, 255, 255, 0.3)',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            🎉 [DEV] Test Confetti
          </button>
        )}
      </div>


      {stats && (
        <div className="floating-avatar" aria-live="polite">
          <div className="floating-avatar-visual">
            <Avatar avatarId={levelAvatarId} size={104} mood="happy" animate />
            <div className="floating-avatar-trail" />
          </div>
          <div className="floating-avatar-info">
            <p className="floating-avatar-name">{levelAvatarId}</p>
            <p className="floating-avatar-meta">Level {stats.user.level} · XP {stats.user.experience.toLocaleString()}</p>
            <p className="floating-avatar-meta">Next lvl {stats.user.progressToNextLevel ?? 0}%</p>
          </div>
        </div>
      )}

      <div
        className="dashboard-header"
        style={{ textAlign: 'center', marginBottom: isMobileView ? 24 : 40 }}
      >
        <h1>Your Dashboard</h1>
        <p>Track your progress and complete daily challenges</p>
      </div>

      <div className="dashboard-grid" style={{ gap: isMobileView ? '20px' : undefined }}>
        {stats && <UserStatsCard stats={stats} />}
        
        <SubscriptionCard />
        
        <ChallengesPanel 
          challenges={challenges} 
          onRefresh={handleRefresh}
        />
        
        {stats && <UserAnalytics stats={stats} />}
      </div>
    </div>
  );
}

function DashboardRoute() {
  return (
    <ProtectedRoute>
      <Dashboard />
    </ProtectedRoute>
  );
}

export const Route = createFileRoute("/dashboard")({
  component: DashboardRoute,
  validateSearch: (search: Record<string, unknown>): { payment?: string } => {
    return {
      payment: typeof search.payment === 'string' ? search.payment : undefined,
    };
  },
});
